import * as React from "react";

type Language = "en" | "hi" | "ta" | "te";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  en: {
    "app.title": "Beat",
    "app.tagline": "Keep Your Beat Strong",
    "header.insights": "Insights",
    "header.family": "Family",
    "header.coach": "AI Coach",
    "header.signOut": "Sign Out",
    "header.language": "Language",
    "header.theme": "Theme",
    "header.english": "English",
    "header.hindi": "Hindi",
    "header.tamil": "Tamil",
    "header.telugu": "Telugu",
    "header.light": "Light",
    "header.dark": "Dark",
    "header.system": "System",
    "accessibility.textSize": "Text Size",
    "accessibility.normal": "Normal",
    "accessibility.large": "Large",
    "dashboard.goodMorning": "Good Morning",
    "dashboard.goodAfternoon": "Good Afternoon",
    "dashboard.goodEvening": "Good Evening",
    "dashboard.tagline": "Let's keep your beat strong today.",
    "dashboard.todaysRituals": "Today's Rituals",
    "dashboard.quickActions": "Quick Actions",
    "dashboard.viewTrends": "View Trends",
    "dashboard.familyDashboard": "Family Dashboard",
    "dashboard.aiCopilot": "Beat AI",
    "dashboard.daysStrong": "Days Strong",
    "dashboard.quickAccess": "Quick Access",
    "dashboard.achievements": "Achievements",
    "dashboard.medications": "Medications",
    "dashboard.shop": "Shop",
    "dashboard.challenges": "Challenges",
    "dashboard.premium": "Premium",
    "ritual.morning": "Morning Ritual",
    "ritual.evening": "Evening Ritual",
    "ritual.morningSubtitle": "With chai ☕",
    "ritual.eveningSubtitle": "After dinner 🍽️",
    "ritual.bloodPressure": "Blood Pressure",
    "ritual.fastingSugar": "Fasting Sugar",
    "ritual.sleepQuality": "Sleep Quality",
    "ritual.medsTaken": "Meds Taken",
    "ritual.randomSugar": "Random Sugar",
    "ritual.stepsCount": "Steps Count",
    "ritual.stressLevel": "Stress Level",
    "ritual.startRitual": "Start Ritual",
    "ritual.completed": "Completed",
    "ritual.eveningComingSoon": "Evening ritual coming soon!",
    "heartScore.title": "Your HeartScore",
    "heartScore.excellent": "Excellent!",
    "heartScore.good": "Good progress",
    "heartScore.needsWork": "Needs attention",
    "heartScore.aiExplanation": "What moved your score today",
    "checkin.step": "Step",
    "checkin.of": "of",
    "checkin.bloodPressure": "Blood Pressure",
    "checkin.enterMorningBP": "Enter your morning BP reading",
    "checkin.enterEveningBP": "Enter your evening BP reading",
    "checkin.systolic": "Systolic (Top)",
    "checkin.diastolic": "Diastolic (Bottom)",
    "checkin.mmHg": "mmHg",
    "checkin.fastingSugar": "Fasting Sugar",
    "checkin.randomSugar": "Random Sugar",
    "checkin.enterSugar": "Enter your sugar level",
    "checkin.mgdL": "mg/dL",
    "checkin.optional": "Optional",
    "checkin.sleepQuality": "Sleep Quality",
    "checkin.howDidYouSleep": "How did you sleep last night?",
    "checkin.veryPoor": "Very Poor",
    "checkin.poor": "Poor",
    "checkin.fair": "Fair",
    "checkin.good": "Good",
    "checkin.excellent": "Excellent",
    "checkin.medications": "Medications",
    "checkin.didYouTakeMeds": "Did you take your prescribed medications?",
    "checkin.yes": "Yes",
    "checkin.no": "No",
    "checkin.stepsCount": "Steps Count",
    "checkin.enterSteps": "Enter your steps count for today",
    "checkin.steps": "steps",
    "checkin.stressLevel": "Stress Level",
    "checkin.howStressed": "How stressed do you feel?",
    "checkin.low": "Low",
    "checkin.moderate": "Moderate",
    "checkin.high": "High",
    "checkin.veryHigh": "Very High",
    "checkin.next": "Next",
    "checkin.back": "Back",
    "checkin.complete": "Complete Ritual",
    "checkin.completedSuccess": "Ritual completed!",
    "coach.title": "Beat AI",
    "coach.subtitle": "Your medical-grade AI health coach",
    "coach.placeholder": "Ask me anything about your health...",
    "coach.send": "Send",
    "coach.thinking": "Thinking...",
    "coach.newChat": "New Chat",
    "coach.disclaimer": "This is AI guidance, not medical diagnosis. Consult a doctor for serious symptoms.",
    "coach.hello": "Hello! I'm Beat",
    "coach.askAnything": "Ask me anything about your health. I'm here to help.",
    "common.loading": "Loading...",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.close": "Close",
    "common.success": "Success!",
    "common.error": "Something went wrong",
    "family.title": "Family Dashboard",
    "family.subtitle": "Keep track of your loved ones' health",
    "family.addMember": "Add Family Member",
    "family.noMembers": "No Family Members Yet",
    "family.addFirst": "Add Your First Member",
    "family.relationship": "Relationship",
    "family.permissions": "Permissions",
    "family.canView": "Can View Health Data",
    "family.canNudge": "Can Send Nudges",
    "onboarding.welcome": "Welcome to Beat!",
    "onboarding.subtitle": "Keep Your Beat Strong",
    "onboarding.language": "Choose your language",
    "onboarding.continue": "Continue",
    "onboarding.basicInfo": "Basic Information",
    "onboarding.basicInfoDesc": "Tell us about yourself",
    "onboarding.fullName": "Full Name",
    "onboarding.namePlaceholder": "Enter your name",
    "onboarding.dateOfBirth": "Date of Birth",
    "onboarding.gender": "Gender",
    "onboarding.genderPlaceholder": "Select gender",
    "onboarding.male": "Male",
    "onboarding.female": "Female",
    "onboarding.other": "Other",
    "onboarding.height": "Height (cm)",
    "onboarding.weight": "Weight (kg)",
    "onboarding.healthGoals": "Health Goals",
    "onboarding.healthGoalsDesc": "What would you like to track?",
    "onboarding.hasHypertension": "I have high blood pressure",
    "onboarding.hasDiabetes": "I have diabetes",
    "onboarding.hasHeartDisease": "I have heart disease",
    "onboarding.ritualTimes": "Daily Check-in Times",
    "onboarding.ritualTimesDesc": "When should we remind you?",
    "onboarding.morningTime": "Morning Check-in",
    "onboarding.eveningTime": "Evening Check-in",
    "onboarding.finish": "Get Started",
    "onboarding.success": "Welcome to Beat!",
    "onboarding.error": "Failed to complete onboarding",
    "insights.title": "Health Insights",
    "insights.subtitle": "Track your health trends and patterns",
    "insights.pdfReport": "PDF Report",
    "insights.avgBP": "Avg BP",
    "insights.avgSugar": "Avg Sugar",
    "insights.heartScore": "HeartScore",
    "insights.avgSteps": "Avg Steps",
    "insights.aiInsights": "AI Health Insights",
    "insights.personalized": "Personalized analysis based on your health data",
    "insights.heartScoreTrend": "HeartScore Trend (30 Days)",
    "insights.bpTrend": "Blood Pressure Trend (30 Days)",
    "insights.sugarTrend": "Blood Sugar Trend (30 Days)",
    "challenges.title": "Health Challenges",
    "challenges.subtitle": "Build healthy habits together with the community",
    "challenges.featured": "Featured Challenges",
    "challenges.myChallenges": "My Challenges",
    "challenges.allChallenges": "All Challenges",
    "challenges.join": "Join",
    "challenges.leave": "Leave",
    "challenges.joined": "Joined",
    "challenges.daysLeft": "days left",
    "challenges.participants": "participants",
    "shop.title": "Health Store",
    "shop.subtitle": "Recommended products for your health journey",
    "shop.featured": "Featured Products",
    "shop.viewProduct": "View Product",
    "subscription.title": "Beat Premium",
    "subscription.subtitle": "Unlock your full health potential",
    "subscription.free": "Free",
    "subscription.basic": "Basic",
    "subscription.premium": "Premium",
    "subscription.currentPlan": "Current Plan",
    "subscription.upgrade": "Upgrade",
    "subscription.perMonth": "/month",
    "profile.title": "Profile",
    "profile.settings": "Settings",
    "profile.notifications": "Notifications",
    "profile.connectDevices": "Connect Devices",
    "profile.exportData": "Export My Data",
    "profile.deleteAccount": "Delete Account",
    "profile.signOut": "Sign Out",
    "export.title": "Data Export",
    "export.bpLogs": "Blood Pressure Logs",
    "export.behaviorLogs": "Behavior & Ritual Logs",
    "export.heartScores": "HeartScore History",
    "export.medications": "Medications",
    "export.fullExport": "Full Export (JSON)",
    "export.shareDoctor": "Share 30-Day Report with Doctor",
    "export.noData": "No data found",
    "export.success": "Data exported successfully",
    "export.failed": "Export failed",
    "offline.savedLocally": "Saved locally — will sync when online",
    "offline.synced": "Synced offline readings",
    "offline.backOnline": "Back online — syncing data...",
  },
  hi: {
    "app.title": "बीट",
    "app.tagline": "अपना बीट मजबूत रखें",
    "header.insights": "जानकारी",
    "header.family": "परिवार",
    "header.coach": "AI सहायक",
    "header.signOut": "साइन आउट",
    "header.language": "भाषा",
    "header.theme": "थीम",
    "header.english": "English",
    "header.hindi": "हिंदी",
    "header.tamil": "தமிழ்",
    "header.telugu": "తెలుగు",
    "header.light": "लाइट",
    "header.dark": "डार्क",
    "header.system": "सिस्टम",
    "accessibility.textSize": "टेक्स्ट साइज़",
    "accessibility.normal": "सामान्य",
    "accessibility.large": "बड़ा",
    "dashboard.goodMorning": "सुप्रभात",
    "dashboard.goodAfternoon": "नमस्ते",
    "dashboard.goodEvening": "शुभ संध्या",
    "dashboard.tagline": "आइए आज अपने दिल को मजबूत रखें।",
    "dashboard.todaysRituals": "आज की दिनचर्या",
    "dashboard.quickActions": "त्वरित कार्य",
    "dashboard.viewTrends": "रुझान देखें",
    "dashboard.familyDashboard": "परिवार डैशबोर्ड",
    "dashboard.aiCopilot": "बीट AI",
    "dashboard.daysStrong": "दिन मजबूत",
    "dashboard.quickAccess": "त्वरित पहुंच",
    "dashboard.achievements": "उपलब्धियां",
    "dashboard.medications": "दवाइयां",
    "dashboard.shop": "शॉप",
    "dashboard.challenges": "चुनौतियां",
    "dashboard.premium": "प्रीमियम",
    "ritual.morning": "सुबह की दिनचर्या",
    "ritual.evening": "शाम की दिनचर्या",
    "ritual.morningSubtitle": "चाय के साथ ☕",
    "ritual.eveningSubtitle": "रात के खाने के बाद 🍽️",
    "ritual.bloodPressure": "रक्तचाप",
    "ritual.fastingSugar": "खाली पेट शुगर",
    "ritual.sleepQuality": "नींद की गुणवत्ता",
    "ritual.medsTaken": "दवाई ली",
    "ritual.randomSugar": "रैंडम शुगर",
    "ritual.stepsCount": "कदमों की गिनती",
    "ritual.stressLevel": "तनाव का स्तर",
    "ritual.startRitual": "शुरू करें",
    "ritual.completed": "पूरा हुआ",
    "ritual.eveningComingSoon": "शाम की दिनचर्या जल्द आ रही है!",
    "heartScore.title": "आपका हार्टस्कोर",
    "heartScore.excellent": "बहुत बढ़िया!",
    "heartScore.good": "अच्छी प्रगति",
    "heartScore.needsWork": "ध्यान देने की जरूरत",
    "heartScore.aiExplanation": "आज आपके स्कोर में क्या बदलाव आया",
    "checkin.step": "चरण",
    "checkin.of": "में से",
    "checkin.bloodPressure": "रक्तचाप",
    "checkin.enterMorningBP": "अपनी सुबह की BP रीडिंग दर्ज करें",
    "checkin.enterEveningBP": "अपनी शाम की BP रीडिंग दर्ज करें",
    "checkin.systolic": "सिस्टोलिक (ऊपर)",
    "checkin.diastolic": "डायस्टोलिक (नीचे)",
    "checkin.mmHg": "mmHg",
    "checkin.fastingSugar": "खाली पेट शुगर",
    "checkin.randomSugar": "रैंडम शुगर",
    "checkin.enterSugar": "अपनी शुगर का स्तर दर्ज करें",
    "checkin.mgdL": "mg/dL",
    "checkin.optional": "वैकल्पिक",
    "checkin.sleepQuality": "नींद की गुणवत्ता",
    "checkin.howDidYouSleep": "कल रात आपकी नींद कैसी रही?",
    "checkin.veryPoor": "बहुत खराब",
    "checkin.poor": "खराब",
    "checkin.fair": "ठीक",
    "checkin.good": "अच्छी",
    "checkin.excellent": "बेहतरीन",
    "checkin.medications": "दवाइयां",
    "checkin.didYouTakeMeds": "क्या आपने अपनी दवाइयां ली हैं?",
    "checkin.yes": "हाँ",
    "checkin.no": "नहीं",
    "checkin.stepsCount": "कदमों की गिनती",
    "checkin.enterSteps": "आज के कदमों की संख्या दर्ज करें",
    "checkin.steps": "कदम",
    "checkin.stressLevel": "तनाव का स्तर",
    "checkin.howStressed": "आप कितना तनाव महसूस कर रहे हैं?",
    "checkin.low": "कम",
    "checkin.moderate": "मध्यम",
    "checkin.high": "उच्च",
    "checkin.veryHigh": "बहुत उच्च",
    "checkin.next": "आगे",
    "checkin.back": "पीछे",
    "checkin.complete": "पूरा करें",
    "checkin.completedSuccess": "दिनचर्या पूरी हुई!",
    "coach.title": "बीट AI",
    "coach.subtitle": "आपका व्यक्तिगत स्वास्थ्य सलाहकार",
    "coach.placeholder": "अपने स्वास्थ्य के बारे में कुछ भी पूछें...",
    "coach.send": "भेजें",
    "coach.thinking": "सोच रहे हैं...",
    "coach.newChat": "नई चैट",
    "coach.disclaimer": "यह AI सलाह है, चिकित्सा निदान नहीं। गंभीर लक्षणों के लिए डॉक्टर से मिलें।",
    "coach.hello": "नमस्ते! मैं बीट हूं",
    "coach.askAnything": "अपने स्वास्थ्य के बारे में कोई भी सवाल पूछें।",
    "common.loading": "लोड हो रहा है...",
    "common.save": "सेव करें",
    "common.cancel": "रद्द करें",
    "common.delete": "हटाएं",
    "common.edit": "संपादित करें",
    "common.close": "बंद करें",
    "common.success": "सफलता!",
    "common.error": "कुछ गलत हो गया",
    "family.title": "परिवार डैशबोर्ड",
    "family.subtitle": "अपने प्रियजनों के स्वास्थ्य पर नज़र रखें",
    "family.addMember": "परिवार का सदस्य जोड़ें",
    "family.noMembers": "अभी तक कोई परिवार का सदस्य नहीं",
    "family.addFirst": "अपना पहला सदस्य जोड़ें",
    "family.relationship": "रिश्ता",
    "family.permissions": "अनुमतियां",
    "family.canView": "स्वास्थ्य डेटा देख सकते हैं",
    "family.canNudge": "नज़ भेज सकते हैं",
    "onboarding.welcome": "बीट में आपका स्वागत है!",
    "onboarding.subtitle": "अपना बीट मजबूत रखें",
    "onboarding.language": "अपनी भाषा चुनें",
    "onboarding.continue": "जारी रखें",
    "onboarding.basicInfo": "बुनियादी जानकारी",
    "onboarding.basicInfoDesc": "हमें अपने बारे में बताएं",
    "onboarding.fullName": "पूरा नाम",
    "onboarding.namePlaceholder": "अपना नाम दर्ज करें",
    "onboarding.dateOfBirth": "जन्म तिथि",
    "onboarding.gender": "लिंग",
    "onboarding.genderPlaceholder": "लिंग चुनें",
    "onboarding.male": "पुरुष",
    "onboarding.female": "महिला",
    "onboarding.other": "अन्य",
    "onboarding.height": "ऊंचाई (सेमी)",
    "onboarding.weight": "वजन (किग्रा)",
    "onboarding.healthGoals": "स्वास्थ्य लक्ष्य",
    "onboarding.healthGoalsDesc": "आप क्या ट्रैक करना चाहेंगे?",
    "onboarding.hasHypertension": "मुझे उच्च रक्तचाप है",
    "onboarding.hasDiabetes": "मुझे मधुमेह है",
    "onboarding.hasHeartDisease": "मुझे हृदय रोग है",
    "onboarding.ritualTimes": "दैनिक चेक-इन समय",
    "onboarding.ritualTimesDesc": "हमें आपको कब याद दिलाना चाहिए?",
    "onboarding.morningTime": "सुबह की चेक-इन",
    "onboarding.eveningTime": "शाम की चेक-इन",
    "onboarding.finish": "शुरू करें",
    "onboarding.success": "बीट में आपका स्वागत है!",
    "onboarding.error": "ऑनबोर्डिंग पूरी करने में विफल",
    "insights.title": "स्वास्थ्य इनसाइट्स",
    "insights.subtitle": "अपने स्वास्थ्य रुझान देखें",
    "insights.pdfReport": "PDF रिपोर्ट",
    "insights.avgBP": "औसत BP",
    "insights.avgSugar": "औसत शुगर",
    "insights.heartScore": "हार्टस्कोर",
    "insights.avgSteps": "औसत कदम",
    "insights.aiInsights": "AI स्वास्थ्य इनसाइट्स",
    "insights.personalized": "आपके स्वास्थ्य डेटा पर आधारित व्यक्तिगत विश्लेषण",
    "insights.heartScoreTrend": "हार्टस्कोर ट्रेंड (30 दिन)",
    "insights.bpTrend": "रक्तचाप ट्रेंड (30 दिन)",
    "insights.sugarTrend": "रक्त शर्करा ट्रेंड (30 दिन)",
    "challenges.title": "स्वास्थ्य चुनौतियां",
    "challenges.subtitle": "समुदाय के साथ मिलकर स्वस्थ आदतें बनाएं",
    "challenges.featured": "लोकप्रिय चुनौतियां",
    "challenges.myChallenges": "मेरी चुनौतियां",
    "challenges.allChallenges": "सभी चुनौतियां",
    "challenges.join": "शामिल हों",
    "challenges.leave": "छोड़ें",
    "challenges.joined": "शामिल",
    "challenges.daysLeft": "दिन बाकी",
    "challenges.participants": "प्रतिभागी",
    "shop.title": "स्वास्थ्य स्टोर",
    "shop.subtitle": "आपकी स्वास्थ्य यात्रा के लिए अनुशंसित उत्पाद",
    "shop.featured": "विशेष उत्पाद",
    "shop.viewProduct": "उत्पाद देखें",
    "subscription.title": "बीट प्रीमियम",
    "subscription.subtitle": "अपनी पूरी स्वास्थ्य क्षमता अनलॉक करें",
    "subscription.free": "मुफ्त",
    "subscription.basic": "बेसिक",
    "subscription.premium": "प्रीमियम",
    "subscription.currentPlan": "वर्तमान प्लान",
    "subscription.upgrade": "अपग्रेड",
    "subscription.perMonth": "/महीना",
    "profile.title": "प्रोफ़ाइल",
    "profile.settings": "सेटिंग्स",
    "profile.notifications": "सूचनाएं",
    "profile.connectDevices": "डिवाइस कनेक्ट करें",
    "profile.exportData": "मेरा डेटा एक्सपोर्ट करें",
    "profile.deleteAccount": "अकाउंट हटाएं",
    "profile.signOut": "साइन आउट",
    "export.title": "डेटा एक्सपोर्ट",
    "export.bpLogs": "रक्तचाप लॉग",
    "export.behaviorLogs": "व्यवहार लॉग",
    "export.heartScores": "हार्टस्कोर इतिहास",
    "export.medications": "दवाइयां",
    "export.fullExport": "पूरा डेटा (JSON)",
    "export.shareDoctor": "डॉक्टर के साथ 30-दिन रिपोर्ट शेयर करें",
    "export.noData": "कोई डेटा नहीं मिला",
    "export.success": "डेटा एक्सपोर्ट हुआ",
    "export.failed": "एक्सपोर्ट विफल",
    "offline.savedLocally": "स्थानीय रूप से सहेजा — ऑनलाइन होने पर सिंक होगा",
    "offline.synced": "ऑफलाइन रीडिंग सिंक हुईं",
    "offline.backOnline": "वापस ऑनलाइन — डेटा सिंक हो रहा है...",
  },
  ta: {
    "app.title": "பீட்",
    "app.tagline": "உங்கள் இதயத்தை வலுவாக வைக்கவும்",
    "header.insights": "நுண்ணறிவுகள்",
    "header.family": "குடும்பம்",
    "header.coach": "AI பயிற்சியாளர்",
    "header.signOut": "வெளியேறு",
    "header.language": "மொழி",
    "header.theme": "தீம்",
    "header.english": "English",
    "header.hindi": "हिंदी",
    "header.tamil": "தமிழ்",
    "header.telugu": "తెలుగు",
    "header.light": "வெளிச்சம்",
    "header.dark": "இருள்",
    "header.system": "சிஸ்டம்",
    "accessibility.textSize": "எழுத்து அளவு",
    "accessibility.normal": "சாதாரணம்",
    "accessibility.large": "பெரியது",
    "dashboard.goodMorning": "காலை வணக்கம்",
    "dashboard.goodAfternoon": "மதிய வணக்கம்",
    "dashboard.goodEvening": "மாலை வணக்கம்",
    "dashboard.tagline": "இன்று உங்கள் இதயத்தை வலுவாக வைப்போம்.",
    "dashboard.todaysRituals": "இன்றைய பழக்கங்கள்",
    "dashboard.quickActions": "விரைவு செயல்கள்",
    "dashboard.viewTrends": "போக்குகளைக் காண",
    "dashboard.familyDashboard": "குடும்ப டாஷ்போர்டு",
    "dashboard.aiCopilot": "பீட் AI",
    "dashboard.daysStrong": "நாட்கள் வலுவாக",
    "dashboard.quickAccess": "விரைவு அணுகல்",
    "dashboard.achievements": "சாதனைகள்",
    "dashboard.medications": "மருந்துகள்",
    "dashboard.shop": "கடை",
    "dashboard.challenges": "சவால்கள்",
    "dashboard.premium": "பிரீமியம்",
    "ritual.morning": "காலை பழக்கம்",
    "ritual.evening": "மாலை பழக்கம்",
    "ritual.morningSubtitle": "சாய உடன் ☕",
    "ritual.eveningSubtitle": "இரவு உணவுக்குப் பிறகு 🍽️",
    "ritual.bloodPressure": "இரத்த அழுத்தம்",
    "ritual.fastingSugar": "வெறும் வயிற்றில் சர்க்கரை",
    "ritual.sleepQuality": "தூக்க தரம்",
    "ritual.medsTaken": "மருந்து எடுத்தேன்",
    "ritual.randomSugar": "ரேண்டம் சர்க்கரை",
    "ritual.stepsCount": "அடிகள் எண்ணிக்கை",
    "ritual.stressLevel": "மன அழுத்த நிலை",
    "ritual.startRitual": "தொடங்கு",
    "ritual.completed": "முடிந்தது",
    "ritual.eveningComingSoon": "மாலை பழக்கம் விரைவில் வரும்!",
    "heartScore.title": "உங்கள் ஹார்ட்ஸ்கோர்",
    "heartScore.excellent": "அருமை!",
    "heartScore.good": "நல்ல முன்னேற்றம்",
    "heartScore.needsWork": "கவனிக்க வேண்டும்",
    "heartScore.aiExplanation": "இன்று உங்கள் மதிப்பெண்ணை என்ன மாற்றியது",
    "checkin.step": "படி",
    "checkin.of": "இல்",
    "checkin.bloodPressure": "இரத்த அழுத்தம்",
    "checkin.enterMorningBP": "உங்கள் காலை BP அளவீடு உள்ளிடவும்",
    "checkin.enterEveningBP": "உங்கள் மாலை BP அளவீடு உள்ளிடவும்",
    "checkin.systolic": "சிஸ்டாலிக் (மேல்)",
    "checkin.diastolic": "டையஸ்டாலிக் (கீழ்)",
    "checkin.mmHg": "mmHg",
    "checkin.fastingSugar": "வெறும் வயிற்றில் சர்க்கரை",
    "checkin.randomSugar": "ரேண்டம் சர்க்கரை",
    "checkin.enterSugar": "உங்கள் சர்க்கரை அளவை உள்ளிடவும்",
    "checkin.mgdL": "mg/dL",
    "checkin.optional": "விருப்பமான",
    "checkin.sleepQuality": "தூக்க தரம்",
    "checkin.howDidYouSleep": "நேற்று இரவு எப்படி தூங்கினீர்கள்?",
    "checkin.veryPoor": "மிகவும் மோசம்",
    "checkin.poor": "மோசம்",
    "checkin.fair": "சரி",
    "checkin.good": "நன்று",
    "checkin.excellent": "அருமை",
    "checkin.medications": "மருந்துகள்",
    "checkin.didYouTakeMeds": "உங்கள் மருந்துகளை எடுத்தீர்களா?",
    "checkin.yes": "ஆம்",
    "checkin.no": "இல்லை",
    "checkin.stepsCount": "அடிகள் எண்ணிக்கை",
    "checkin.enterSteps": "இன்றைய அடிகள் எண்ணிக்கையை உள்ளிடவும்",
    "checkin.steps": "அடிகள்",
    "checkin.stressLevel": "மன அழுத்த நிலை",
    "checkin.howStressed": "எவ்வளவு மன அழுத்தம் உணர்கிறீர்கள்?",
    "checkin.low": "குறைவு",
    "checkin.moderate": "மிதமான",
    "checkin.high": "அதிகம்",
    "checkin.veryHigh": "மிக அதிகம்",
    "checkin.next": "அடுத்து",
    "checkin.back": "பின்னால்",
    "checkin.complete": "முடிக்கவும்",
    "checkin.completedSuccess": "பழக்கம் முடிந்தது!",
    "coach.title": "பீட் AI",
    "coach.subtitle": "உங்கள் AI சுகாதார பயிற்சியாளர்",
    "coach.placeholder": "உங்கள் ஆரோக்கியத்தைப் பற்றி கேளுங்கள்...",
    "coach.send": "அனுப்பு",
    "coach.thinking": "யோசிக்கிறேன்...",
    "coach.newChat": "புதிய சாட்",
    "coach.disclaimer": "இது AI ஆலோசனை, மருத்துவ நோயறிதல் அல்ல. தீவிர அறிகுறிகளுக்கு மருத்துவரை அணுகவும்.",
    "coach.hello": "வணக்கம்! நான் பீட்",
    "coach.askAnything": "உங்கள் ஆரோக்கியத்தைப் பற்றி எதையும் கேளுங்கள்.",
    "common.loading": "ஏற்றுகிறது...",
    "common.save": "சேமி",
    "common.cancel": "ரத்துசெய்",
    "common.delete": "நீக்கு",
    "common.edit": "திருத்து",
    "common.close": "மூடு",
    "common.success": "வெற்றி!",
    "common.error": "ஏதோ தவறு நடந்தது",
    "family.title": "குடும்ப டாஷ்போர்டு",
    "family.subtitle": "உங்கள் அன்பானவர்களின் ஆரோக்கியத்தைக் கண்காணிக்கவும்",
    "family.addMember": "குடும்ப உறுப்பினர் சேர்க்க",
    "family.noMembers": "இதுவரை குடும்ப உறுப்பினர்கள் இல்லை",
    "family.addFirst": "முதல் உறுப்பினரைச் சேர்க்கவும்",
    "family.relationship": "உறவு",
    "family.permissions": "அனுமதிகள்",
    "family.canView": "ஆரோக்கிய தரவைப் பார்க்கலாம்",
    "family.canNudge": "நட்ஜ் அனுப்பலாம்",
    "onboarding.welcome": "பீட்டுக்கு வரவேற்கிறோம்!",
    "onboarding.subtitle": "உங்கள் இதயத்தை வலுவாக வைக்கவும்",
    "onboarding.language": "உங்கள் மொழியைத் தேர்வு செய்யுங்கள்",
    "onboarding.continue": "தொடர்",
    "onboarding.basicInfo": "அடிப்படை தகவல்",
    "onboarding.basicInfoDesc": "உங்களைப் பற்றி சொல்லுங்கள்",
    "onboarding.fullName": "முழு பெயர்",
    "onboarding.namePlaceholder": "உங்கள் பெயரை உள்ளிடவும்",
    "onboarding.dateOfBirth": "பிறந்த தேதி",
    "onboarding.gender": "பாலினம்",
    "onboarding.genderPlaceholder": "பாலினத்தைத் தேர்வு செய்யுங்கள்",
    "onboarding.male": "ஆண்",
    "onboarding.female": "பெண்",
    "onboarding.other": "மற்றவை",
    "onboarding.height": "உயரம் (செ.மீ)",
    "onboarding.weight": "எடை (கிலோ)",
    "onboarding.healthGoals": "சுகாதார இலக்குகள்",
    "onboarding.healthGoalsDesc": "எதைக் கண்காணிக்க விரும்புகிறீர்கள்?",
    "onboarding.hasHypertension": "உயர் இரத்த அழுத்தம் உள்ளது",
    "onboarding.hasDiabetes": "நீரிழிவு நோய் உள்ளது",
    "onboarding.hasHeartDisease": "இதய நோய் உள்ளது",
    "onboarding.ritualTimes": "தினசரி செக்-இன் நேரங்கள்",
    "onboarding.ritualTimesDesc": "எப்போது நினைவூட்ட வேண்டும்?",
    "onboarding.morningTime": "காலை செக்-இன்",
    "onboarding.eveningTime": "மாலை செக்-இன்",
    "onboarding.finish": "தொடங்குவோம்",
    "onboarding.success": "பீட்டுக்கு வரவேற்கிறோம்!",
    "onboarding.error": "செயல்முறை தோல்வியடைந்தது",
    "insights.title": "ஆரோக்கிய நுண்ணறிவுகள்",
    "insights.subtitle": "உங்கள் ஆரோக்கிய போக்குகளைக் கண்காணிக்கவும்",
    "insights.pdfReport": "PDF அறிக்கை",
    "insights.avgBP": "சராசரி BP",
    "insights.avgSugar": "சராசரி சர்க்கரை",
    "insights.heartScore": "ஹார்ட்ஸ்கோர்",
    "insights.avgSteps": "சராசரி அடிகள்",
    "insights.aiInsights": "AI ஆரோக்கிய நுண்ணறிவுகள்",
    "insights.personalized": "உங்கள் ஆரோக்கிய தரவின் அடிப்படையில் தனிப்பட்ட பகுப்பாய்வு",
    "insights.heartScoreTrend": "ஹார்ட்ஸ்கோர் போக்கு (30 நாட்கள்)",
    "insights.bpTrend": "இரத்த அழுத்த போக்கு (30 நாட்கள்)",
    "insights.sugarTrend": "இரத்த சர்க்கரை போக்கு (30 நாட்கள்)",
    "challenges.title": "ஆரோக்கிய சவால்கள்",
    "challenges.subtitle": "சமூகத்துடன் சேர்ந்து ஆரோக்கிய பழக்கங்களை உருவாக்குங்கள்",
    "challenges.featured": "சிறப்பு சவால்கள்",
    "challenges.myChallenges": "என் சவால்கள்",
    "challenges.allChallenges": "அனைத்து சவால்கள்",
    "challenges.join": "சேர்",
    "challenges.leave": "விடு",
    "challenges.joined": "சேர்ந்தது",
    "challenges.daysLeft": "நாட்கள் மீதம்",
    "challenges.participants": "பங்கேற்பாளர்கள்",
    "shop.title": "ஆரோக்கிய கடை",
    "shop.subtitle": "உங்கள் ஆரோக்கிய பயணத்திற்கான பரிந்துரைகள்",
    "shop.featured": "சிறப்பு பொருட்கள்",
    "shop.viewProduct": "பொருளைப் பார்",
    "subscription.title": "பீட் பிரீமியம்",
    "subscription.subtitle": "உங்கள் முழு ஆரோக்கிய திறனை அன்லாக் செய்யுங்கள்",
    "subscription.free": "இலவசம்",
    "subscription.basic": "அடிப்படை",
    "subscription.premium": "பிரீமியம்",
    "subscription.currentPlan": "தற்போதைய திட்டம்",
    "subscription.upgrade": "மேம்படுத்து",
    "subscription.perMonth": "/மாதம்",
    "profile.title": "சுயவிவரம்",
    "profile.settings": "அமைப்புகள்",
    "profile.notifications": "அறிவிப்புகள்",
    "profile.connectDevices": "சாதனங்களை இணைக்க",
    "profile.exportData": "என் தரவை ஏற்றுமதி செய்",
    "profile.deleteAccount": "கணக்கை நீக்கு",
    "profile.signOut": "வெளியேறு",
    "export.title": "தரவு ஏற்றுமதி",
    "export.bpLogs": "இரத்த அழுத்த பதிவுகள்",
    "export.behaviorLogs": "நடத்தை பதிவுகள்",
    "export.heartScores": "ஹார்ட்ஸ்கோர் வரலாறு",
    "export.medications": "மருந்துகள்",
    "export.fullExport": "முழு ஏற்றுமதி (JSON)",
    "export.shareDoctor": "30-நாள் அறிக்கையை மருத்துவருடன் பகிர்",
    "export.noData": "தரவு இல்லை",
    "export.success": "தரவு ஏற்றுமதி வெற்றி",
    "export.failed": "ஏற்றுமதி தோல்வி",
    "offline.savedLocally": "உள்ளூரில் சேமிக்கப்பட்டது — ஆன்லைன் வரும்போது சிங்க் ஆகும்",
    "offline.synced": "ஆஃப்லைன் அளவீடுகள் சிங்க் ஆனது",
    "offline.backOnline": "மீண்டும் ஆன்லைன் — தரவு சிங்க் ஆகிறது...",
  },
  te: {
    "app.title": "బీట్",
    "app.tagline": "మీ హృదయాన్ని బలంగా ఉంచండి",
    "header.insights": "అంతర్దృష్టులు",
    "header.family": "కుటుంబం",
    "header.coach": "AI కోచ్",
    "header.signOut": "సైన్ అవుట్",
    "header.language": "భాష",
    "header.theme": "థీమ్",
    "header.english": "English",
    "header.hindi": "हिंदी",
    "header.tamil": "தமிழ்",
    "header.telugu": "తెలుగు",
    "header.light": "లైట్",
    "header.dark": "డార్క్",
    "header.system": "సిస్టమ్",
    "accessibility.textSize": "టెక్స్ట్ సైజ్",
    "accessibility.normal": "సాధారణం",
    "accessibility.large": "పెద్దది",
    "dashboard.goodMorning": "శుభోదయం",
    "dashboard.goodAfternoon": "శుభ మధ్యాహ్నం",
    "dashboard.goodEvening": "శుభ సాయంత్రం",
    "dashboard.tagline": "ఈ రోజు మీ హృదయాన్ని బలంగా ఉంచుదాం.",
    "dashboard.todaysRituals": "ఈ రోజు అలవాట్లు",
    "dashboard.quickActions": "వేగ చర్యలు",
    "dashboard.viewTrends": "ట్రెండ్‌లు చూడండి",
    "dashboard.familyDashboard": "కుటుంబ డాష్‌బోర్డ్",
    "dashboard.aiCopilot": "బీట్ AI",
    "dashboard.daysStrong": "రోజులు బలంగా",
    "dashboard.quickAccess": "వేగ ప్రాప్తి",
    "dashboard.achievements": "సాధనలు",
    "dashboard.medications": "మందులు",
    "dashboard.shop": "షాప్",
    "dashboard.challenges": "సవాళ్ళు",
    "dashboard.premium": "ప్రీమియం",
    "ritual.morning": "ఉదయం అలవాటు",
    "ritual.evening": "సాయంత్రం అలవాటు",
    "ritual.morningSubtitle": "చాయ్‌తో ☕",
    "ritual.eveningSubtitle": "రాత్రి భోజనం తర్వాత 🍽️",
    "ritual.bloodPressure": "రక్తపోటు",
    "ritual.fastingSugar": "ఖాళీ కడుపు షుగర్",
    "ritual.sleepQuality": "నిద్ర నాణ్యత",
    "ritual.medsTaken": "మందు తీసుకున్నారు",
    "ritual.randomSugar": "రాండమ్ షుగర్",
    "ritual.stepsCount": "అడుగుల లెక్క",
    "ritual.stressLevel": "ఒత్తిడి స్థాయి",
    "ritual.startRitual": "ప్రారంభించు",
    "ritual.completed": "పూర్తయింది",
    "ritual.eveningComingSoon": "సాయంత్రం అలవాటు త్వరలో!",
    "heartScore.title": "మీ హార్ట్‌స్కోర్",
    "heartScore.excellent": "అద్భుతం!",
    "heartScore.good": "మంచి పురోగతి",
    "heartScore.needsWork": "శ్రద్ధ అవసరం",
    "heartScore.aiExplanation": "ఈ రోజు మీ స్కోర్‌ను ఏమి మార్చింది",
    "checkin.step": "దశ",
    "checkin.of": "లో",
    "checkin.bloodPressure": "రక్తపోటు",
    "checkin.enterMorningBP": "మీ ఉదయం BP రీడింగ్ నమోదు చేయండి",
    "checkin.enterEveningBP": "మీ సాయంత్రం BP రీడింగ్ నమోదు చేయండి",
    "checkin.systolic": "సిస్టాలిక్ (పై)",
    "checkin.diastolic": "డయాస్టాలిక్ (కింద)",
    "checkin.mmHg": "mmHg",
    "checkin.fastingSugar": "ఖాళీ కడుపు షుగర్",
    "checkin.randomSugar": "రాండమ్ షుగర్",
    "checkin.enterSugar": "మీ షుగర్ స్థాయిని నమోదు చేయండి",
    "checkin.mgdL": "mg/dL",
    "checkin.optional": "ఐచ్ఛికం",
    "checkin.sleepQuality": "నిద్ర నాణ్యత",
    "checkin.howDidYouSleep": "నిన్న రాత్రి ఎలా నిద్రపోయారు?",
    "checkin.veryPoor": "చాలా చెడ్డది",
    "checkin.poor": "చెడ్డది",
    "checkin.fair": "సరే",
    "checkin.good": "మంచిది",
    "checkin.excellent": "అద్భుతం",
    "checkin.medications": "మందులు",
    "checkin.didYouTakeMeds": "మీ మందులు తీసుకున్నారా?",
    "checkin.yes": "అవును",
    "checkin.no": "లేదు",
    "checkin.stepsCount": "అడుగుల లెక్క",
    "checkin.enterSteps": "ఈ రోజు అడుగుల లెక్కను నమోదు చేయండి",
    "checkin.steps": "అడుగులు",
    "checkin.stressLevel": "ఒత్తిడి స్థాయి",
    "checkin.howStressed": "మీకు ఎంత ఒత్తిడి ఉంది?",
    "checkin.low": "తక్కువ",
    "checkin.moderate": "మధ్యస్థం",
    "checkin.high": "ఎక్కువ",
    "checkin.veryHigh": "చాలా ఎక్కువ",
    "checkin.next": "తదుపరి",
    "checkin.back": "వెనుకకు",
    "checkin.complete": "పూర్తి చేయి",
    "checkin.completedSuccess": "అలవాటు పూర్తయింది!",
    "coach.title": "బీట్ AI",
    "coach.subtitle": "మీ AI ఆరోగ్య కోచ్",
    "coach.placeholder": "మీ ఆరోగ్యం గురించి ఏదైనా అడగండి...",
    "coach.send": "పంపు",
    "coach.thinking": "ఆలోచిస్తున్నాను...",
    "coach.newChat": "కొత్త చాట్",
    "coach.disclaimer": "ఇది AI సలహా, వైద్య నిర్ధారణ కాదు. తీవ్ర లక్షణాలకు వైద్యుడిని సంప్రదించండి.",
    "coach.hello": "నమస్కారం! నేను బీట్",
    "coach.askAnything": "మీ ఆరోగ్యం గురించి ఏదైనా అడగండి.",
    "common.loading": "లోడ్ అవుతోంది...",
    "common.save": "సేవ్ చేయి",
    "common.cancel": "రద్దు చేయి",
    "common.delete": "తొలగించు",
    "common.edit": "సవరించు",
    "common.close": "మూసివేయి",
    "common.success": "విజయం!",
    "common.error": "ఏదో తప్పు జరిగింది",
    "family.title": "కుటుంబ డాష్‌బోర్డ్",
    "family.subtitle": "మీ ప్రియమైనవారి ఆరోగ్యాన్ని కనిపెట్టండి",
    "family.addMember": "కుటుంబ సభ్యుడిని జోడించండి",
    "family.noMembers": "ఇంకా కుటుంబ సభ్యులు లేరు",
    "family.addFirst": "మీ మొదటి సభ్యుడిని జోడించండి",
    "family.relationship": "సంబంధం",
    "family.permissions": "అనుమతులు",
    "family.canView": "ఆరోగ్య డేటా చూడగలరు",
    "family.canNudge": "నడ్జ్ పంపగలరు",
    "onboarding.welcome": "బీట్‌కు స్వాగతం!",
    "onboarding.subtitle": "మీ హృదయాన్ని బలంగా ఉంచండి",
    "onboarding.language": "మీ భాషను ఎంచుకోండి",
    "onboarding.continue": "కొనసాగించు",
    "onboarding.basicInfo": "ప్రాథమిక సమాచారం",
    "onboarding.basicInfoDesc": "మీ గురించి చెప్పండి",
    "onboarding.fullName": "పూర్తి పేరు",
    "onboarding.namePlaceholder": "మీ పేరు నమోదు చేయండి",
    "onboarding.dateOfBirth": "పుట్టిన తేదీ",
    "onboarding.gender": "లింగం",
    "onboarding.genderPlaceholder": "లింగాన్ని ఎంచుకోండి",
    "onboarding.male": "పురుషుడు",
    "onboarding.female": "స్త్రీ",
    "onboarding.other": "ఇతరం",
    "onboarding.height": "ఎత్తు (సెం.మీ)",
    "onboarding.weight": "బరువు (కిలో)",
    "onboarding.healthGoals": "ఆరోగ్య లక్ష్యాలు",
    "onboarding.healthGoalsDesc": "ఏమి ట్రాక్ చేయాలనుకుంటున్నారు?",
    "onboarding.hasHypertension": "నాకు అధిక రక్తపోటు ఉంది",
    "onboarding.hasDiabetes": "నాకు మధుమేహం ఉంది",
    "onboarding.hasHeartDisease": "నాకు గుండె జబ్బు ఉంది",
    "onboarding.ritualTimes": "రోజూ చెక్-ఇన్ సమయాలు",
    "onboarding.ritualTimesDesc": "ఎప్పుడు గుర్తు చేయాలి?",
    "onboarding.morningTime": "ఉదయం చెక్-ఇన్",
    "onboarding.eveningTime": "సాయంత్రం చెక్-ఇన్",
    "onboarding.finish": "ప్రారంభించుదాం",
    "onboarding.success": "బీట్‌కు స్వాగతం!",
    "onboarding.error": "ప్రక్రియ విఫలమైంది",
    "insights.title": "ఆరోగ్య అంతర్దృష్టులు",
    "insights.subtitle": "మీ ఆరోగ్య ట్రెండ్‌లను ట్రాక్ చేయండి",
    "insights.pdfReport": "PDF రిపోర్ట్",
    "insights.avgBP": "సగటు BP",
    "insights.avgSugar": "సగటు షుగర్",
    "insights.heartScore": "హార్ట్‌స్కోర్",
    "insights.avgSteps": "సగటు అడుగులు",
    "insights.aiInsights": "AI ఆరోగ్య అంతర్దృష్టులు",
    "insights.personalized": "మీ ఆరోగ్య డేటా ఆధారంగా వ్యక్తిగత విశ్లేషణ",
    "insights.heartScoreTrend": "హార్ట్‌స్కోర్ ట్రెండ్ (30 రోజులు)",
    "insights.bpTrend": "రక్తపోటు ట్రెండ్ (30 రోజులు)",
    "insights.sugarTrend": "రక్త షుగర్ ట్రెండ్ (30 రోజులు)",
    "challenges.title": "ఆరోగ్య సవాళ్ళు",
    "challenges.subtitle": "సమాజంతో కలిసి ఆరోగ్య అలవాట్లు నిర్మించండి",
    "challenges.featured": "ప్రత్యేక సవాళ్ళు",
    "challenges.myChallenges": "నా సవాళ్ళు",
    "challenges.allChallenges": "అన్ని సవాళ్ళు",
    "challenges.join": "చేరు",
    "challenges.leave": "వదిలివేయి",
    "challenges.joined": "చేరారు",
    "challenges.daysLeft": "రోజులు మిగిలి ఉన్నాయి",
    "challenges.participants": "పాల్గొనేవారు",
    "shop.title": "ఆరోగ్య షాప్",
    "shop.subtitle": "మీ ఆరోగ్య ప్రయాణానికి సిఫార్సు చేసిన ఉత్పత్తులు",
    "shop.featured": "ప్రత్యేక ఉత్పత్తులు",
    "shop.viewProduct": "ఉత్పత్తిని చూడండి",
    "subscription.title": "బీట్ ప్రీమియం",
    "subscription.subtitle": "మీ పూర్తి ఆరోగ్య సామర్థ్యాన్ని అన్‌లాక్ చేయండి",
    "subscription.free": "ఉచితం",
    "subscription.basic": "బేసిక్",
    "subscription.premium": "ప్రీమియం",
    "subscription.currentPlan": "ప్రస్తుత ప్లాన్",
    "subscription.upgrade": "అప్‌గ్రేడ్",
    "subscription.perMonth": "/నెల",
    "profile.title": "ప్రొఫైల్",
    "profile.settings": "సెట్టింగ్‌లు",
    "profile.notifications": "నోటిఫికేషన్లు",
    "profile.connectDevices": "పరికరాలను కనెక్ట్ చేయి",
    "profile.exportData": "నా డేటాను ఎక్స్‌పోర్ట్ చేయి",
    "profile.deleteAccount": "ఖాతాను తొలగించు",
    "profile.signOut": "సైన్ అవుట్",
    "export.title": "డేటా ఎక్స్‌పోర్ట్",
    "export.bpLogs": "రక్తపోటు రికార్డులు",
    "export.behaviorLogs": "ప్రవర్తన రికార్డులు",
    "export.heartScores": "హార్ట్‌స్కోర్ చరిత్ర",
    "export.medications": "మందులు",
    "export.fullExport": "పూర్తి ఎక్స్‌పోర్ట్ (JSON)",
    "export.shareDoctor": "30-రోజుల రిపోర్ట్‌ను డాక్టర్‌తో షేర్ చేయండి",
    "export.noData": "డేటా కనుగొనబడలేదు",
    "export.success": "డేటా ఎక్స్‌పోర్ట్ విజయవంతం",
    "export.failed": "ఎక్స్‌పోర్ట్ విఫలమైంది",
    "offline.savedLocally": "స్థానికంగా సేవ్ చేయబడింది — ఆన్‌లైన్ అయినప్పుడు సింక్ అవుతుంది",
    "offline.synced": "ఆఫ్‌లైన్ రీడింగ్‌లు సింక్ అయ్యాయి",
    "offline.backOnline": "తిరిగి ఆన్‌లైన్ — డేటా సింక్ అవుతోంది...",
  },
};

const LanguageContext = React.createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const [language, setLanguage] = React.useState<Language>(() => {
    const saved = localStorage.getItem("beat-language");
    return (saved as Language) || "en";
  });

  React.useEffect(() => {
    localStorage.setItem("beat-language", language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key: string): string => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = React.useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};
